import { useEffect, useState } from 'react';
import { getConversationIntent, setConversationIntent, type ConversationIntent } from '../lib/intents-api';
import { useNotificationsStore } from '../stores/notifications';

// Intent field for the conversation inbox bar: what the site's local intent
// model reads this chat as (live while it is open), and a picker for the agent
// to correct it — a correction re-tags the chat and becomes a training example.
// Renders nothing until the label list loads, or when the server has none.
export function IntentField({ sessionId, className }: { sessionId: string; className?: string }) {
  const [intent, setIntent] = useState<ConversationIntent | null>(null);
  const [labels, setLabels] = useState<Array<{ key: string; name: string }>>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getConversationIntent(sessionId)
      .then((r) => {
        if (cancelled) return;
        setIntent(r.intent);
        setLabels(r.labels);
      })
      .catch(() => {
        if (!cancelled) setLabels([]);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  async function correct(key: string) {
    const previous = intent;
    setIntent({ key, confidence: null, source: 'agent' });
    setBusy(true);
    try {
      await setConversationIntent(sessionId, key);
    } catch {
      setIntent(previous);
      useNotificationsStore.getState().push({
        kind: 'system',
        title: 'Couldn’t change the intent',
        body: 'The server rejected the change. Please try again.',
        ttl: 5000,
      });
    } finally {
      setBusy(false);
    }
  }

  if (labels.length === 0) return null;
  const hint =
    intent?.source === 'agent'
      ? 'Set by an agent'
      : intent?.confidence != null
        ? `Model confidence ${Math.round(intent.confidence * 100)}%`
        : 'Not classified yet';
  return (
    <label className={className} title={hint}>
      <span>
        Intent
        {intent?.source === 'model' && intent.confidence != null ? ` · ${Math.round(intent.confidence * 100)}%` : ''}
      </span>
      <select
        value={intent?.key ?? ''}
        disabled={busy}
        aria-label="Conversation intent"
        onChange={(e) => {
          if (e.target.value) void correct(e.target.value);
        }}
      >
        {!intent && <option value="">Not classified</option>}
        {labels.map((l) => (
          <option key={l.key} value={l.key}>
            {l.name}
          </option>
        ))}
      </select>
    </label>
  );
}
